/**
 * Realtime API - Socket.IO connection for live updates.
 * Handles: conversation count, notifications, agent online/offline status.
 *
 * Socket Events (listen):
 *   'conversation:count'      → { count: 5 }
 *   'notification:new'        → { type: 'alert', customerName: '...', conversationId: 1 }
 *   'notification:clear'      → {}
 *   'agent:status:changed'    → { status: 'online' | 'offline' }
 *
 * Socket Events (emit):
 *   'agent:status:update'     → { status: 'online' | 'offline' }
 *
 * REST Endpoints (fallback):
 *   GET  /chat/api/conversations/count    → { count: 5 }
 *   GET  /bot/api/agent_status            → { online: true/false }
 *   POST /bot/api/agent_status            → { online: true/false } → returns updated value
 */

import { io } from 'socket.io-client';
import { getToken, getStoredUser } from './auth';
import { getBaseUrl } from './utils';

let socket = null;

// ---- Socket Connection ----

/**
 * Initialize socket connection with auth token.
 * Call this after successful login.
 */
export function connectSocket() {
  if (socket?.connected) return socket;

  const token = getToken();
  if (!token) return null;

  const base = getBaseUrl();
  socket = io(base, {
    path: '/bot/socket.io',
    auth: { token },
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
  });

  socket.on('connect', () => {
    console.log('Socket connected:', socket.id);
    // Join the company room so company-scoped server emits reach this socket.
    // Fires on both the initial connect and every reconnect.
    const user = getStoredUser();
    if (user?.company_id) {
      socket.emit('join_company', { company_id: user.company_id });
    }
  });

  socket.on('connect_error', (err) => {
    console.warn('Socket connection error:', err.message);
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
  });

  return socket;
}

/**
 * Disconnect socket. Call on logout.
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Get current socket instance.
 */
export function getSocket() {
  return socket;
}

// ---- Event Listeners ----

/**
 * Listen for conversation count updates.
 * @param {function} callback - receives { count: number }
 */
export function onConversationCount(callback) {
  if (!socket) return;
  socket.on('conversation:count', callback);
  return () => socket.off('conversation:count', callback);
}

/**
 * Listen for new notifications.
 * @param {function} callback - receives notification object
 */
export function onNotification(callback) {
  if (!socket) return;
  socket.on('notification:new', callback);
  return () => socket.off('notification:new', callback);
}

/**
 * Listen for agent status changes (from other tabs/devices).
 * @param {function} callback - receives { status: 'online' | 'offline' }
 */
export function onAgentStatusChanged(callback) {
  if (!socket) return;
  socket.on('agent:status:changed', callback);
  return () => socket.off('agent:status:changed', callback);
}

// ---- Emit Events ----

/**
 * Update agent online/offline status via socket.
 * @param {'online' | 'offline'} status
 */
export function emitAgentStatus(status) {
  // Always persist via REST API
  updateAgentStatusREST(status);
  // Also emit socket event for real-time sync to other tabs/devices
  if (socket?.connected) {
    socket.emit('agent:status:update', { status });
  }
}

// ---- REST Fallbacks ----

async function parseJson(res) {
  const data = await res.json();
  if (!res.ok) throw new Error(data?.message || 'Request failed');
  if (data.status === 'success' && data.data) return data.data;
  return data.data != null ? data.data : data;
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}`, Accept: 'application/json' } : { Accept: 'application/json' };
}

/**
 * Fetch conversation count via REST (fallback if socket not connected).
 * GET /chat/api/conversations/count
 * @returns {Promise<number>}
 */
export async function getConversationCount() {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/api/conversations/count`.replace(/([^:]\/)\/+/g, '$1');
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return 0;
    const data = await res.json().catch(() => ({}));
    return data.count || 0;
  } catch {
    return 0;
  }
}

/**
 * Update agent status via REST.
 * POST /bot/api/agent_status
 * @param {'online' | 'offline'} status
 */
export async function updateAgentStatusREST(status) {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/api/agent_status`.replace(/([^:]\/)\/+/g, '$1');
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ online: status === 'online' }),
    });
  } catch (err) {
    console.warn('Failed to update agent status:', err);
  }
}

/**
 * Get current agent status via REST.
 * GET /bot/api/agent_status
 * @returns {Promise<string>} 'online' or 'offline'
 */
export async function getAgentStatus() {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/api/agent_status`.replace(/([^:]\/)\/+/g, '$1');
    const res = await fetch(url, { headers: authHeaders() });
    const data = await parseJson(res);
    return data.online ? 'online' : 'offline';
  } catch (err) {
    console.warn('Failed to fetch agent status:', err);
    return 'online';
  }
}
